/**
 * Token Helper
 * Handles MSAL token acquisition with automatic fallback to interactive login
 */
/** Error codes thrown by acquireToken itself (in addition to MSAL's own). */
export const AUTH_LOOP_DETECTED = 'auth_loop_detected';
export const REDIRECT_IN_PROGRESS = 'redirect_in_progress';
/**
 * Error codes that indicate we need to redirect to interactive login
 */
const REDIRECT_ERROR_CODES = new Set([
    // InteractionRequiredAuthError codes
    'interaction_required',
    'consent_required',
    'login_required',
    // BrowserAuthError codes
    'no_tokens_found',
    'no_account_error',
    'token_refresh_required',
    'user_cancelled',
    'monitor_window_timeout',
    'popup_window_error',
    'empty_navigate_uri',
    'hash_empty_error',
    'interaction_in_progress',
    // ClientAuthError codes
    'no_cached_authority_error',
    'no_account_in_silent_request',
    'multiple_matching_tokens',
    'invalid_client_credential',
    // ServerError codes
    'invalid_grant',
    'expired_token',
    'invalid_token',
]);
/**
 * Check if an error requires interactive login
 */
function getErrorDetails(error) {
    const typedError = error;
    return {
        errorCode: typedError?.errorCode,
        message: typedError?.message,
        name: typedError?.name,
    };
}
function requiresInteractiveLogin(error) {
    const { errorCode, message, name } = getErrorDetails(error);
    const normalizedCode = errorCode?.toLowerCase();
    // Check error code for known redirect-required codes
    if (normalizedCode && REDIRECT_ERROR_CODES.has(normalizedCode)) {
        return true;
    }
    // Check error message for common patterns
    const errorMessage = message?.toLowerCase() || '';
    if (errorMessage.includes('interaction_required') ||
        errorMessage.includes('login_required') ||
        errorMessage.includes('consent_required') ||
        errorMessage.includes('invalid_grant') ||
        (errorMessage.includes('token') && errorMessage.includes('expired')) ||
        (errorMessage.includes('refresh token') && errorMessage.includes('expired')) ||
        errorMessage.includes('token acquisition in iframe failed') ||
        errorMessage.includes('monitor_window_timeout')) {
        return true;
    }
    if (name && name.toLowerCase().includes('autherror')) {
        return true;
    }
    return false;
}
/**
 * Clear any stuck MSAL interaction state from localStorage
 */
function clearInteractionState() {
    if (typeof window === 'undefined')
        return;
    if (window.location.hash)
        return;
    const storages = [];
    try {
        storages.push(localStorage);
    }
    catch {
        // Ignore storage access errors.
    }
    try {
        storages.push(sessionStorage);
    }
    catch {
        // Ignore storage access errors.
    }
    storages.forEach((storage) => {
        const keysToRemove = Object.keys(storage).filter((key) => key.includes('msal.interaction.status') ||
            key.includes('msal.temp') ||
            key.includes('.request.'));
        keysToRemove.forEach((key) => storage.removeItem(key));
    });
}
const REDIRECT_ATTEMPT_KEY = 'sfc.auth.redirectAttempt';
const REDIRECT_LOOP_WINDOW_MS = 60_000;
/**
 * Single-flight latch: once any caller has initiated acquireTokenRedirect,
 * concurrent callers must not start a second one (or clear the first one's
 * in-flight request state, which corrupts the response validation on return).
 */
let redirectInFlight = false;
function makeAuthError(errorCode, message) {
    const error = new Error(message);
    error.errorCode = errorCode;
    return error;
}
function lastRedirectAttemptMs() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(REDIRECT_ATTEMPT_KEY);
        if (!raw)
            return null;
        const ts = Number(raw);
        return Number.isFinite(ts) ? ts : null;
    }
    catch {
        return null;
    }
}
function markRedirectAttempt() {
    if (typeof window === 'undefined')
        return;
    try {
        sessionStorage.setItem(REDIRECT_ATTEMPT_KEY, String(Date.now()));
    }
    catch {
        // Ignore storage access errors.
    }
}
/**
 * Drop the cached MSAL accounts/tokens so the app falls back to a clean,
 * user-initiated sign-in instead of redirect-looping on a broken cache.
 */
async function clearMsalCache(instance) {
    try {
        if (instance.clearCache) {
            await instance.clearCache();
            return;
        }
    }
    catch {
        // Fall through to manual cleanup.
    }
    if (typeof window === 'undefined')
        return;
    try {
        Object.keys(localStorage)
            .filter((key) => key.startsWith('msal.') || key.includes('-login.windows.net-') || key.includes('login.microsoftonline.com'))
            .forEach((key) => localStorage.removeItem(key));
    }
    catch {
        // Ignore storage access errors.
    }
}
/**
 * Acquire access token with automatic fallback to redirect if silent fails
 * @param instance - MSAL PublicClientApplication instance
 * @param request - Token request configuration (scopes, account, etc.)
 * @param options - interactive: false disables the redirect fallback
 * @returns Promise with access token or redirects to login
 */
export async function acquireToken(instance, request, options = {}) {
    try {
        const response = await instance.acquireTokenSilent(request);
        return { accessToken: response.accessToken };
    }
    catch (error) {
        const { errorCode, message } = getErrorDetails(error);
        const normalizedCode = errorCode?.toLowerCase();
        console.log('[TokenHelper] Silent token acquisition failed:', errorCode, message);
        if (normalizedCode === 'monitor_window_timeout') {
            console.warn('[TokenHelper] Silent iframe timeout, falling back to interactive login.');
        }
        if (normalizedCode === 'interaction_in_progress') {
            console.warn('[TokenHelper] Interaction already in progress, skipping redirect.');
            throw error;
        }
        if (requiresInteractiveLogin(error)) {
            if (options.interactive === false) {
                // Caller opted out of interactive fallback — surface the silent failure.
                throw error;
            }
            if (redirectInFlight) {
                // Another caller already started the redirect; do not start a second
                // one or touch its interaction state.
                throw makeAuthError(REDIRECT_IN_PROGRESS, 'An interactive login redirect is already in progress.');
            }
            const lastAttempt = lastRedirectAttemptMs();
            if (lastAttempt !== null && Date.now() - lastAttempt < REDIRECT_LOOP_WINDOW_MS) {
                // We redirected to the IdP less than a minute ago and silent
                // acquisition still fails — redirecting again would loop until the
                // IdP blocks us. Drop the broken cache and let the app route the
                // user to its login page for a clean, manual sign-in.
                console.error('[TokenHelper] Redirect loop detected; clearing MSAL cache.');
                await clearMsalCache(instance);
                throw makeAuthError(AUTH_LOOP_DETECTED, 'Authentication redirect loop detected; cached credentials were cleared.');
            }
            console.log('[TokenHelper] Redirecting to interactive login...');
            redirectInFlight = true;
            markRedirectAttempt();
            // Clear any stuck interaction state before redirecting
            clearInteractionState();
            // Small delay to ensure state is cleared
            await new Promise((resolve) => setTimeout(resolve, 100));
            try {
                // Redirect to login - this will navigate away
                await instance.acquireTokenRedirect(request);
            }
            catch (redirectError) {
                redirectInFlight = false;
                throw redirectError;
            }
            // This line should never be reached due to redirect
            throw new Error('Redirecting to login...');
        }
        // Unknown error - log details and rethrow
        console.error('[TokenHelper] Unhandled token error:', error);
        throw error;
    }
}
//# sourceMappingURL=tokenHelper.js.map